import { useState, useEffect, useCallback } from "react";

interface UseTokenInputParams {
   value?: string;
   onChange?: (val: string) => void;
}

export const useTokenInput = ({ value = "", onChange }: UseTokenInputParams) => {
   const [inputValue, setInputValue] = useState(value);
   const [clickedFields, setClickedFields] = useState<string[]>([]);

   useEffect(() => {
      if (value !== inputValue) {
         setInputValue(value);

         const tokens = value.match(/\{([^}]+)\}/g)?.map((t) => t.replace(/[{}]/g, "")) || [];
         setClickedFields(tokens);
      }
   }, [value]);

   const updateClickedFields = useCallback((val: string) => {
      const tokens = val.match(/\{([^}]+)\}/g)?.map((t) => t.replace(/[{}]/g, "")) || [];
      setClickedFields(tokens);
   }, []);

   const handleChange = useCallback(
      (val: string) => {
         const brokenTokens = val.match(/\{[^}]*$|^[^{]*\}/g) || [];
         brokenTokens.forEach((token) => {
            val = val.replace(token, "");
         });

         setInputValue(val);
         updateClickedFields(val);

         if (onChange) onChange(val);
      },
      [onChange, updateClickedFields],
   );

   const handleCursor = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement> | React.MouseEvent<any>) => {
         const el = e.target as HTMLInputElement | HTMLTextAreaElement;
         const val = el.value;
         const cursorPos = el.selectionStart || 0;

         const tokenRegex = /\{[^}]+\}/g;
         let match: RegExpExecArray | null;

         while ((match = tokenRegex.exec(val)) !== null) {
            const start = match.index;
            const end = start + match[0].length;

            if (cursorPos > start && cursorPos < end) {
               el.selectionStart = end;
               el.selectionEnd = end;
               e.preventDefault();
               break;
            }
         }
      },
      [],
   );

   const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
         const el = e.target as HTMLInputElement | HTMLTextAreaElement;
         const val = el.value;
         const cursorPos = el.selectionStart || 0;

         if (e.key !== "Backspace" && e.key !== "Delete") return;

         const tokenRegex = /\{[^}]+\}/g;
         let match: RegExpExecArray | null;

         while ((match = tokenRegex.exec(val)) !== null) {
            const start = match.index;
            const end = start + match[0].length;

            if (cursorPos >= start && cursorPos <= end) {
               e.preventDefault();

               const newVal = val.slice(0, start) + val.slice(end);
               setInputValue(newVal);
               updateClickedFields(newVal);

               if (onChange) onChange(newVal);

               setTimeout(() => {
                  el.selectionStart = start;
                  el.selectionEnd = start;
               }, 0);
               break;
            }
         }
      },
      [onChange, updateClickedFields],
   );

   return {
      inputValue,
      clickedFields,
      setInputValue: handleChange,
      handleChange,
      handleCursor,
      handleKeyDown,
   };
};
